/**
 * ══════════════════════════════════════════════════════════════════════════
 *  Verificación · los mensajes que ve el consumidor (#90)
 * ══════════════════════════════════════════════════════════════════════════
 *
 * Una marca de vinos no habla como un laboratorio. Esta pantalla deja que cada
 * marca escriba su propia copia para los seis momentos de la página pública,
 * en los tres idiomas, y la vea en un teléfono antes de publicarla.
 *
 * Lo que esta pantalla NO ofrece, y lo dice en voz alta
 * ----------------------------------------------------
 * No hay sección para «producto falsificado» ni para «producto robado». Es la
 * regla 4 del dominio y no es una preferencia de redacción: un falso positivo
 * con ese texto le dice a un comprador legítimo que lo que tiene en la mano es
 * ilegítimo, en base a una inferencia. Los tres casos colapsan en «no pudimos
 * verificar este código» más el canal de reporte; la certeza aparece en la
 * bandeja de alertas de esta misma marca, con la evidencia.
 *
 * La tarjeta `WhyNoAccusation` está para que quien viene con la maqueta del
 * socio en la mano encuentre la respuesta acá y no en un ticket.
 *
 * Convenciones que usa (ver `web/dashboard/README.md`)
 * ----------------------------------------------------
 *   · datos por TanStack Query                → `useApiQuery` + `useMutation`
 *   · clave de cache en un solo lugar         → `@/api/keys`
 *   · carga / error / vacío resueltos         → `@/ui/states`
 *   · rol mínimo declarado en la ruta         → `routes.tsx`
 *   · textos en el catálogo, con prefijo      → `verification.*`
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState, type ReactNode } from 'react';

import type { ApiError } from '@/api/errors';
import { useApiQuery } from '@/api/hooks';
import { verificationKeys } from '@/api/keys';
import { useApi } from '@/api/useApi';
import { useAuth } from '@/auth/useAuth';
import { describeApiError } from '@/i18n/errorMessages';
import { useI18n } from '@/i18n/useI18n';
import { useTenantSettings } from '@/tenant/useTenantSettings';
import { Badge } from '@/ui/Badge';
import { Button } from '@/ui/Button';
import { Card, CardBody, CardHeader } from '@/ui/Card';
import { ErrorState, LoadingState } from '@/ui/states';
import { Field, TextField } from '../products/ui/fields';
import { PhonePreview } from './PhonePreview';
import {
  BODY_MAX,
  LOCALES,
  PREVIEW_STATES,
  SLOT_META,
  TITLE_MAX,
  draftFrom,
  emptyDraft,
  isDirty,
  previewUrl,
  rejectionsOf,
  toPayload,
  type CopyLocale,
  type CopySlot,
  type Draft,
  type PreviewState,
  type VerificationMessages,
} from './copy';

/**
 * De dónde se sirve la página del consumidor.
 *
 * En producción es el mismo dominio de la marca; en local, el `astro dev` del
 * repo. Se resuelve por variable de entorno para no hornear una URL de
 * desarrollo en el build del dashboard.
 */
const CONSUMER_BASE =
  (import.meta.env['VITE_CONSUMER_BASE'] as string | undefined) ?? 'http://localhost:4322';

/**
 * Cuánto se espera después de la última tecla para recargar la vista previa.
 *
 * El borrador viaja en la URL del `<iframe>`, así que cada cambio lo remonta:
 * recargarlo por pulsación sería un parpadeo constante. Medio segundo es el
 * punto donde el cambio se siente inmediato al dejar de escribir.
 */
const PREVIEW_DEBOUNCE_MS = 500;

export function VerificationPage(): ReactNode {
  const { t } = useI18n();
  const { can } = useAuth();
  const api = useApi();
  const queryClient = useQueryClient();
  const tenant = useTenantSettings();

  const query = useApiQuery<VerificationMessages>(
    verificationKeys.messages(),
    '/api/v1/verification/messages',
  );

  const [locale, setLocale] = useState<CopyLocale>('es');
  const [state, setState] = useState<PreviewState>('genuine');
  const [draft, setDraft] = useState<Draft>(emptyDraft);
  const [saved, setSaved] = useState<Draft>(emptyDraft);
  const [sembradoCon, setSembradoCon] = useState<VerificationMessages | undefined>(undefined);

  // El borrador se siembra con lo publicado cuando la respuesta CAMBIA de
  // identidad: la primera vez que llega y después de cada guardado.
  //
  // Va en el render y no en un `useEffect` a propósito. Con un efecto, cada
  // revalidación en segundo plano de TanStack Query pisaría lo que la persona
  // está escribiendo, y además dispara un render en cascada (es lo que marca
  // `react-hooks/set-state-in-effect`). Ajustar el estado durante el render
  // cuando la entrada cambia es el patrón que documenta React para esto.
  if (query.data !== undefined && query.data !== sembradoCon) {
    const inicial = draftFrom(query.data);
    setSembradoCon(query.data);
    setDraft(inicial);
    setSaved(inicial);
  }

  const save = useMutation<VerificationMessages, ApiError, Draft>({
    // `toPayload` deja fuera las ranuras vacías: vaciar un campo es volver al
    // texto por defecto, no guardar una cadena en blanco.
    //
    // `api.request` y no un `api.put`: el cliente expone GET/POST/PATCH/DELETE
    // y agregarle un verbo por este módulo tocaría el archivo que comparten
    // los diez. `request` es la primitiva pública sobre la que están escritos
    // los cuatro, con el mismo manejo de 401, refresh y errores.
    mutationFn: (value) =>
      api.request<VerificationMessages>('/api/v1/verification/messages', {
        method: 'PUT',
        body: toPayload(value),
      }),
    onSuccess: (result) => {
      const guardado = draftFrom(result);
      setDraft(guardado);
      setSaved(guardado);
      void queryClient.invalidateQueries({ queryKey: verificationKeys.all });
    },
  });

  const src = useDebouncedPreviewUrl({
    base: CONSUMER_BASE,
    state,
    locale,
    tenantSlug: tenant.slug,
    draft,
  });

  const rechazos = useMemo(
    () => (save.error === null ? [] : rejectionsOf(save.error.details)),
    [save.error],
  );
  const sucio = isDirty(draft, saved);
  const editable = can('admin');

  const setSlot = (slot: CopySlot, field: 'title' | 'body', value: string): void => {
    setDraft((prev) => ({
      ...prev,
      [locale]: {
        ...prev[locale],
        [slot]: { ...prev[locale][slot], [field]: value },
      },
    }));
  };

  return (
    <div className="mx-auto max-w-6xl">
      <WhyNoAccusation />

      {query.isPending && <LoadingState rows={4} />}

      {query.isError && (
        <Card className="mt-4">
          <CardBody>
            <ErrorState
              error={query.error}
              onRetry={() => {
                void query.refetch();
              }}
            />
          </CardBody>
        </Card>
      )}

      {!query.isPending && !query.isError && (
        <div className="mt-4 grid gap-4 lg:grid-cols-[minmax(0,1fr)_360px]">
          <Card>
            <CardHeader
              title={t('verification.title')}
              description={t('verification.description')}
              actions={
                <>
                  {!editable && <Badge tone="neutral">{t('verification.readOnly')}</Badge>}
                  {editable && (
                    <Button
                      variant="primary"
                      size="sm"
                      disabled={!sucio || save.isPending}
                      onClick={() => {
                        save.mutate(draft);
                      }}
                    >
                      {save.isPending ? t('verification.saving') : t('verification.save')}
                    </Button>
                  )}
                </>
              }
            />

            <CardBody className="border-b border-border">
              <LocaleTabs locale={locale} onChange={setLocale} />
            </CardBody>

            {save.isSuccess && !sucio && (
              <CardBody className="border-b border-border">
                <p role="status" className="text-sm font-medium text-verified">
                  {t('verification.saved')}
                </p>
              </CardBody>
            )}

            {save.error !== null && (
              <CardBody className="border-b border-border">
                <RejectionNotice error={save.error} rejections={rechazos} />
              </CardBody>
            )}

            <CardBody className="space-y-6">
              {SLOT_META.map((meta) => {
                // En un `const` para que TypeScript lo estreche dentro del
                // `onClick`: `meta.preview` es `PreviewState | null` y ahí
                // adentro ya no se acuerda del `!== null` de arriba.
                const estado = meta.preview;
                return (
                  <fieldset key={meta.slot} className="space-y-2">
                    <legend className="text-sm font-semibold text-ink">
                      {t(meta.labelKey)}
                      {estado !== null && (
                        <button
                          type="button"
                          className="ml-2 text-xs font-normal text-accent underline-offset-2 hover:underline"
                          onClick={() => {
                            setState(estado);
                          }}
                        >
                          {t('verification.slot.seeIt')}
                        </button>
                      )}
                    </legend>
                    <p className="max-w-prose text-xs text-ink-subtle">{t(meta.hintKey)}</p>

                    {meta.hasTitle && (
                      <TextField
                        label={t('verification.field.title')}
                        value={draft[locale][meta.slot].title}
                        onChange={(value) => {
                          setSlot(meta.slot, 'title', value);
                        }}
                        maxLength={TITLE_MAX}
                        placeholder={t('verification.field.defaultPlaceholder')}
                        disabled={!editable}
                        error={errorFor(rechazos, locale, meta.slot, 'title', t)}
                      />
                    )}

                    <Field
                      label={t('verification.field.body')}
                      hint={
                        meta.slot === 'already_seen'
                          ? t('verification.field.bodyVars')
                          : t('verification.field.bodyHint')
                      }
                      error={errorFor(rechazos, locale, meta.slot, 'body', t)}
                    >
                      {(props) => (
                        <textarea
                          {...props}
                          rows={3}
                          maxLength={BODY_MAX}
                          disabled={!editable}
                          placeholder={t('verification.field.defaultPlaceholder')}
                          value={draft[locale][meta.slot].body}
                          onChange={(event) => {
                            setSlot(meta.slot, 'body', event.target.value);
                          }}
                        />
                      )}
                    </Field>
                  </fieldset>
                );
              })}
            </CardBody>
          </Card>

          <div className="space-y-4">
            <Card>
              <CardHeader
                title={t('verification.preview.title')}
                description={t('verification.preview.description')}
              />
              <CardBody className="space-y-3">
                <StateTabs state={state} onChange={setState} />
                <PhonePreview src={src} title={t('verification.preview.frameTitle')} />
              </CardBody>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────── selectores

function LocaleTabs({
  locale,
  onChange,
}: {
  locale: CopyLocale;
  onChange: (value: CopyLocale) => void;
}): ReactNode {
  const { t } = useI18n();

  return (
    <div className="flex flex-wrap items-center gap-2" role="group" aria-label={t('nav.language')}>
      {LOCALES.map((value) => (
        <Button
          key={value}
          size="sm"
          variant={value === locale ? 'primary' : 'secondary'}
          aria-pressed={value === locale}
          onClick={() => {
            onChange(value);
          }}
        >
          {t(`verification.locale.${value}` as const)}
        </Button>
      ))}
      <p className="text-xs text-ink-subtle">{t('verification.locale.hint')}</p>
    </div>
  );
}

function StateTabs({
  state,
  onChange,
}: {
  state: PreviewState;
  onChange: (value: PreviewState) => void;
}): ReactNode {
  const { t } = useI18n();

  return (
    <div
      className="flex flex-wrap gap-2"
      role="group"
      aria-label={t('verification.preview.states')}
    >
      {PREVIEW_STATES.map((value) => (
        <Button
          key={value}
          size="sm"
          variant={value === state ? 'primary' : 'secondary'}
          aria-pressed={value === state}
          onClick={() => {
            onChange(value);
          }}
        >
          {t(`verification.state.${value}` as const)}
        </Button>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────── por qué no hay acusación

/**
 * La respuesta a la pregunta que trae quien vio la maqueta.
 *
 * Va arriba de todo y no escondida en un `hint`: sin ella, la conversación se
 * repite en cada marca nueva, y la respuesta correcta ("no se puede, y por
 * eso") es más larga que un tooltip.
 */
function WhyNoAccusation(): ReactNode {
  const { t } = useI18n();

  return (
    <Card className="mt-4 border-accent">
      <CardHeader title={t('verification.rule.title')} />
      <CardBody className="max-w-prose space-y-2 text-sm text-ink-muted">
        <p>{t('verification.rule.what')}</p>
        <p>{t('verification.rule.why')}</p>
        <p>{t('verification.rule.where')}</p>
        <p className="text-ink">{t('verification.rule.recall')}</p>
      </CardBody>
    </Card>
  );
}

// ───────────────────────────────────────────────────────────── rechazos

function RejectionNotice({
  error,
  rejections,
}: {
  error: ApiError;
  rejections: ReturnType<typeof rejectionsOf>;
}): ReactNode {
  const { t } = useI18n();
  const presentacion = describeApiError(error, t);

  return (
    <div role="alert" className="space-y-2">
      <p className="text-sm font-medium text-critical">{presentacion.title}</p>
      {rejections.length > 0 && (
        <ul className="list-inside list-disc text-xs text-ink-muted">
          {rejections.map((row) => (
            <li key={`${row.locale}.${row.slot}.${row.field}`}>
              {t('verification.rejected.item', {
                locale: row.locale.toUpperCase(),
                slot: row.slot,
                field: row.field,
                terms: row.terms.join(', '),
              })}
            </li>
          ))}
        </ul>
      )}
      {presentacion.traceId !== null && (
        <p className="text-xs text-ink-subtle">{presentacion.traceId}</p>
      )}
    </div>
  );
}

function errorFor(
  rejections: ReturnType<typeof rejectionsOf>,
  locale: string,
  slot: string,
  field: string,
  t: ReturnType<typeof useI18n>['t'],
): string | undefined {
  const hit = rejections.find(
    (row) => row.locale === locale && row.slot === slot && row.field === field,
  );
  return hit === undefined
    ? undefined
    : t('verification.rejected.field', { terms: hit.terms.join(', ') });
}

// ───────────────────────────────────────────────────────────── auxiliares

/**
 * La URL de la vista previa, recalculada medio segundo después de la última
 * tecla. Ver `PREVIEW_DEBOUNCE_MS`.
 */
function useDebouncedPreviewUrl(options: {
  base: string;
  state: PreviewState;
  locale: CopyLocale;
  tenantSlug: string;
  draft: Draft;
}): string {
  const inmediata = previewUrl(options);
  const [url, setUrl] = useState(inmediata);

  useEffect(() => {
    const timer = setTimeout(() => {
      setUrl(inmediata);
    }, PREVIEW_DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [inmediata]);

  return url;
}
